import React, { useState, useEffect, useContext, useRef } from "react";
import { Context } from "context/Wrapper";
import { useFetchCollectionDocs } from "apis/dataSnapshots";
import { db } from "config/config";
import {
    collection,
    query,
    where,
    getDocs,
    onSnapshot,
    or,
} from "firebase/firestore";
import {
    GridComponent,
    ColumnsDirective,
    ColumnDirective,
    Page,
    Sort,
    Filter,
    DetailRow,
    ExcelExport,
    Toolbar,
    Inject
} from "@syncfusion/ej2-react-grids";
import { Box, Typography, Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formattedDate } from "../utils/dateHelpers";

const JobStatusReport = ({ open, onClose }) => {
    const context = useContext(Context);
    const { userId, groupId } = context.state.userProfile || {}; 
    const { showAllDataFlag } = context.state || {};

    const contacts = useFetchCollectionDocs({
        collectionName: "contacts",
        conditions: [true],
        setContext: context.setState
    });

    const buildings = useFetchCollectionDocs({
        collectionName: "buildings",
        conditions: [true],
        setContext: context.setState
    });

    const [projects, setProjects] = useState([]);
    const [workTypes, setWorkTypes] = useState([]);
    const [rows, setRows] = useState([]);

    const gridRef = useRef(null);

    useEffect(() => {
        if (!userId) return;

        const projectsRef = collection(db, "projects");
        
        let baseConditions = [];
        
        if (showAllDataFlag && groupId) {
            baseConditions = [
                or(
                    where("groupId", "==", groupId),
                    where("userId", "==", userId)
                )
            ];
        } else if (!showAllDataFlag && userId) {
            baseConditions = [where("userId", "==", userId)];
        }
        
        const q = query(projectsRef, ...baseConditions);

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            setProjects(docs);
        });

        return () => unsubscribe();
    }, [userId, groupId, showAllDataFlag]); 

    useEffect(() => {
        const fetchWorkTypes = async () => {
            if (!projects || projects.length === 0) {
                setWorkTypes([]);
                return;
            }

            if (showAllDataFlag) {
                const snap = await getDocs(collection(db, "workTypes"));
                const allDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setWorkTypes(allDocs);
                return;
            }

            const allIds = projects.map(p => p.id);
            const chunks = [];
            for (let i = 0; i < allIds.length; i += 10) {
                chunks.push(allIds.slice(i, i + 10));
            }

            let combined = [];
            for (const chunk of chunks) {
                const filters = [
                    where("projectId", "in", chunk),
                    where("userId", "==", userId),
                ];


                const snap = await getDocs(query(collection(db, "workTypes"), ...filters));
                combined = combined.concat(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            }

            setWorkTypes(combined);
        };

        fetchWorkTypes();
    }, [projects, showAllDataFlag, userId]);

    useEffect(() => {
        if (!projects || projects.length === 0) {
            setRows([]);
            return;
        }

        const newRows = [];

        projects.forEach((project) => {
            const matchingWTs = workTypes.filter((wt) => wt.projectId === project.id);

            if (matchingWTs && matchingWTs.length > 0) {
                matchingWTs.forEach((wt) => {
                    const buildingObj = buildings.find((b) => b.id === project.building) || {};
                    const applicantObj = contacts.find((c) => c.id === wt.applicant) || {};
                    const filerObj = contacts.find((c) => c.id === wt.filingRepresentative) || {};

                    newRows.push({
                        rowId: project.id + "_" + wt.id,
                        jobId: project.customerReference || "",
                        address: `${buildingObj.houseNumber || ""} ${buildingObj.streetName || ""}`.trim(),
                        floors: project.floors || "",
                        workType: wt.workType || "",
                        estimatedCost: wt.estimatedCost ? Number(wt.estimatedCost) : null,
                        applicantName: applicantObj.fullName || "",
                        filingRepName: filerObj.fullName || "",
                        dateCreated: formatFirestoreDate(wt.dateCreated),
                        dateApproved: wt.dateApproved || "",
                        permitIssued: wt.permitIssued || "",
                        permitExpiration: wt.permitExpiration || "",
                        signOffRequested: wt.signOffRequested || "",
                        signedOffOn: wt.signedOffOn || "",
                        locDate: wt.locDate || "",
                        dobNumber: wt.dobNumber || "",
                        comments: wt.comments || ""
                    });
                });
            }
        });

        setRows(newRows);
    }, [projects, workTypes, buildings, contacts]);

    const formatFirestoreDate = ts => {
        if (!ts) return "";

        let date;
        if (typeof ts.seconds === "number") {
            date = new Date(ts.seconds * 1000);
        } else {
            date = new Date(ts);
        }

        if (isNaN(date.getTime())) return "";

        // Format as yyyy-mm-dd
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    const columns = [
        { field: "jobId", headerText: "Job ID", textAlign: "Center", headerTextAlign: "Center", },
        { field: "address", headerText: "Address", textAlign: "Left", headerTextAlign: "Center", },
        { field: "floors", headerText: "Floor(s)", textAlign: "Center", headerTextAlign: "Center", },
        { field: "workType", headerText: "Work Type", textAlign: "Center", headerTextAlign: "Center", },
        { field: "estimatedCost", headerText: "Estimated Cost", textAlign: "Center", headerTextAlign: "Center", type: "number", format: "C0" },
        { field: "applicantName", headerText: "Applicant", textAlign: "Center", headerTextAlign: "Center", },
        { field: "filingRepName", headerText: "Filing Rep", textAlign: "Center", headerTextAlign: "Center", },
        { field: "dateCreated", headerText: "Date Filed", textAlign: "Center", headerTextAlign: "Center", },
        { field: "dateApproved", headerText: "Approved", textAlign: "Center", headerTextAlign: "Center", },
        { field: "permitIssued", headerText: "Issued", textAlign: "Center", headerTextAlign: "Center", },
        { field: "permitExpiration", headerText: "Expiration", textAlign: "Center", headerTextAlign: "Center", },
        { field: "signOffRequested", headerText: "Sign-off Requested", textAlign: "Center", headerTextAlign: "Center", },
        { field: "signedOffOn", headerText: "Signed Off On", textAlign: "Center", headerTextAlign: "Center", },
        { field: "locDate", headerText: "LOC", textAlign: "Center", headerTextAlign: "Center", },
        { field: "dobNumber", headerText: "DOB Number", textAlign: "Center", headerTextAlign: "Center", },
        { field: "comments", headerText: "Comments", textAlign: "Left", headerTextAlign: "Center", }
    ];

    // toolbar  export handlers
    const toolbarOptions = ["ExcelExport", "CsvExport"];
    const toolbarClick = (args) => {
        if (!gridRef.current) return;

        if (args.item.id.includes("excelexport")) {
            gridRef.current.excelExport({ exportType: "CurrentPage" });
        }

        if (args.item.id.includes("csvexport")) {
            gridRef.current.csvExport({ exportType: "CurrentPage" });
        }
    };

    const onDataBound = () => {
        if (!gridRef.current) return;
        gridRef.current.autoFitColumns(columns.map((c) => c.field));
    };

    return (
        <Dialog fullWidth maxWidth="xl" open={open} onClose={onClose}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    borderBottom: "1px solid #ddd"
                }}
            >
                <Typography variant="h6">Job Status Report</Typography>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ height: "80vh", width: "100%", p: 1 }}>
                <GridComponent
                    id="JobStatusGrid"
                    ref={gridRef}
                    dataSource={rows}
                    width="100%"
                    allowPaging={true}
                    pageSettings={{
                        pageSize: 20,
                        pageSizes: [10, 20, 50, 100]
                    }}
                    allowSorting={true}
                    allowFiltering={true}
                    allowExcelExport={true}
                    allowCsvExport={true}
                    toolbar={toolbarOptions}
                    toolbarClick={toolbarClick}
                    dataBound={onDataBound}
                >
                    <ColumnsDirective>
                        {columns.map((col) => (
                            <ColumnDirective
                                key={col.field}
                                field={col.field}
                                headerText={col.headerText}
                                width={col.width}
                                textAlign={col.textAlign}
                                headerTextAlign={col.headerTextAlign}
                                type={col.type}
                                format={col.format}
                            />
                        ))}
                    </ColumnsDirective>
                    <Inject
                        services={[Page, Sort, Filter, DetailRow, ExcelExport, Toolbar]}
                    />
                </GridComponent>
            </Box>
        </Dialog>
    );
};

export default JobStatusReport;